import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { tapSelection } from '@/lib/haptics';
import { cn } from '@/lib/utils';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
};

/**
 * Button — Brand book §5.
 * - Primary: deep orange fill with white text, 12px radius.
 * - Secondary: secondary-container @ 20% opacity, on-secondary-container text.
 * - Ghost: transparent, used inside cards / nav.
 * - Sizes: sm (32), md (44), lg (56) — designed to hit accessible tap targets.
 *
 * The primary variant is a solid brand-orange fill (subtle top-down gradient
 * from primary to the pressed orange) with white text — the Figma pill button.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', loading, fullWidth, disabled, children, onClick, ...props }, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          'inline-flex items-center justify-center gap-2 rounded-button font-display font-semibold',
          'transition active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed',
          // Sizes
          size === 'sm' && 'h-8 px-3 text-sm',
          size === 'md' && 'h-11 px-5 text-base',
          size === 'lg' && 'h-14 px-7 text-lg',
          // Variants
          variant === 'primary' &&
            'text-on-primary bg-gradient-to-b from-primary to-primary-pressed shadow-soft hover:shadow-ambient hover:brightness-105',
          variant === 'secondary' &&
            'app-button-secondary text-on-secondary-container bg-secondary-container/20 hover:bg-secondary-container/30',
          variant === 'ghost' &&
            'text-on-surface hover:bg-surface-container-high/60',
          variant === 'danger' &&
            'text-on-error bg-error hover:bg-error/90',
          fullWidth && 'w-full',
          className,
        )}
        {...props}
        // Лёгкая вибрация на главные кнопки — только в приложении (решение владельца 24.09.2026, `lib/haptics.ts`).
        // Только primary: «Начать», «Проверить», «Отправить», «Войти». Второстепенные — без отклика.
        onClick={
          variant === 'primary'
            ? (e) => {
                tapSelection();
                onClick?.(e);
              }
            : onClick
        }
      >
        {loading && (
          <span
            aria-hidden
            className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
          />
        )}
        {/* ⚠️ Надпись — в своём элементе (техдолг 263). Значок загрузки вставляется
            ПЕРЕД ней; если надпись — голая строка, а переводчик браузера уже подменил
            её узел на <font>, React падает на insertBefore («Сохранить» у эссе и
            записи). `contents` — обёртка не создаёт своей коробки, раскладка кнопки
            (flex, gap) та же. */}
        <span className="contents">{children}</span>
      </button>
    );
  },
);

Button.displayName = 'Button';
